<template>
  <b-card
    no-body
    :img-src="toy.image"
    :img-alt="toy.name"
    img-top
    @click="handleCardClick"
  >
    <b-card-title
      class="flex-1 px-2 py-2 m-0 d-flex justify-content-between border-top"
      :class="statusClass"
    >
      <b-dropdown v-if="isActive || isPaused" no-caret class="btn-cancel">
        <template #button-content>
          <span class="fas fa-ellipsis-v" />
        </template>
        <b-dropdown-item @click="onTriggerAction(RENT_ACTIONS.TRANSFER)">
          <span class="fas fa-exchange-alt mr-1" /> Transferir Aluguel
        </b-dropdown-item>
        <b-dropdown-item @click="onAddExtraTime()">
          <span class="fas fa-clock mr-1" />Tempo Extra
        </b-dropdown-item>
        <b-dropdown-item variant="danger" @click="handleCancelClick">
          <span class="fas fa-ban mr-1" /> Cancelar
        </b-dropdown-item>
      </b-dropdown>
      <div class="w-100">
        <small class="d-flex gap-1">
          <span>{{ toy.name }}</span>
          <!-- <em v-if="isPaused"> (pausado) </em> -->
        </small>
        <span
          v-if="isActive || isPaused"
          class="d-flex flex-wrap align-items-center"
        >
          {{
            (toy.rent && toy.rent.customer && toy.rent.customer.name) ||
            'Cliente não encontrado'
          }}
          <span
            v-if="toy.rent && toy.rent.hasSignature"
            v-b-tooltip.hover
            title="Aluguel assinado"
            class="ml-2 text-success"
          >
            <i class="fas fa-signature"></i>
          </span>
          <b-button
            variant="link"
            size="sm"
            :disabled="!toy.rent || !toy.rent.customer"
            @click="openCustomerModal"
          >
            <span class="fas fa-info-circle"></span>
          </b-button>
        </span>
        <!-- Exibe o número do aluguel, mostra o botão de resgate se for 10 ou mais e adiciona checkbox "Não considerar" na direita -->
        <small v-if="isActive || isPaused" class="d-block">
          <div class="d-flex flex-wrap align-items-center gap-1">
            <span class="chip">Aluguel Nº {{ getPoints + 1 }}</span>

            <b-button
              v-if="canRescueRent"
              size="sm"
              variant="outline-secondary"
              class="ml-1"
              :disabled="!toy.rent || !toy.rent.customer"
              @click.stop="onRescueRent"
            >
              Resgatar
            </b-button>

            <div
              v-if="shouldShowLoyaltyDiscountOption"
              class="loyalty-card-toggle"
              :class="{ active: isLoyaltyDiscountActive }"
            >
              <b-form-checkbox
                :checked="isLoyaltyDiscountActive"
                :disabled="!canApplyLoyaltyDiscount"
                class="loyalty-card-check mb-0"
                @click.native.stop
                @change="onLoyaltyDiscountChange"
              >
                <span class="loyalty-card-label">
                  <span class="fas fa-gift loyalty-card-icon"></span>
                  <span>
                    <small class="d-block">Aplicar desconto</small>
                    <small
                      v-if="canApplyLoyaltyDiscount"
                      class="loyalty-card-value"
                    >
                      -{{ loyaltyDiscountAmount | formatCurrency }}
                    </small>
                  </span>
                </span>
              </b-form-checkbox>
              <b-button
                variant="link"
                size="sm"
                class="loyalty-info-button p-0"
                @click.stop="showLoyaltyDiscountInfo"
              >
                <span class="fas fa-info-circle"></span>
              </b-button>
            </div>
          </div>
        </small>
      </div>
    </b-card-title>

    <b-card-text
      v-if="isActive || isPaused"
      class="p-2 m-0"
      :class="statusClass"
    >
      <div class="display-table">
        <div>
          <div class="text-right pr-2">
            <small><b>Período:</b></small>
          </div>
          <div>
            <small class="mr-2">{{ period }}</small>
            <b-button
              size="sm"
              variant="clear-primary"
              class="p-0"
              @click="onChangePeriod"
            >
              <span class="fas fa-redo" />
            </b-button>
          </div>
        </div>
        <div>
          <div class="text-right pr-2">
            <small><b>Tempo:</b></small>
          </div>
          <div>
            <small class="mr-2">{{ getElapsedTime }} min</small>
          </div>
        </div>

        <div v-if="extraTime">
          <div class="text-right pr-2">
            <small><b>Tempo Extra:</b></small>
          </div>
          <div>
            <small class="mr-2">{{ extraTime }} min</small>
          </div>
        </div>

        <div v-if="pausedTime">
          <div class="text-right pr-2">
            <small><b>Pausado:</b></small>
          </div>
          <div>
            <small class="mr-2">{{ pausedTime }} min</small>
          </div>
        </div>

        <div>
          <div class="text-right pr-2">
            <small><b>Início:</b></small>
          </div>
          <div>
            <small>{{ createdAt }}</small>
          </div>
        </div>

        <div>
          <div class="text-right pr-2">
            <small><b>Retorno:</b></small>
          </div>
          <div>
            <small>{{ endsAt }}</small>
          </div>
        </div>

        <div>
          <div class="text-right pr-2">
            <small><b>Valor:</b></small>
          </div>
          <div>
            <small>{{ amount | formatCurrency }}</small>
          </div>
        </div>

        <div v-if="isLoyaltyDiscountActive">
          <div class="text-right pr-2">
            <small><b>Valor final:</b></small>
          </div>
          <div>
            <small class="text-success font-weight-bold">
              {{ finalAmountWithLoyaltyDiscount | formatCurrency }}
            </small>
          </div>
        </div>
      </div>
      <div class="d-flex gap-1 flex-column mt-3">
        <div class="d-flex flex-wrap justify-content-stretch gap-1">
          <b-button
            variant="success"
            class="flex-1"
            @click="onCloseRent(PAYMENT_METHODS_IDS.CASH)"
          >
            DI
          </b-button>
          <b-button
            variant="warning"
            class="flex-1"
            @click="onCloseRent(PAYMENT_METHODS_IDS.CREDIT_CARD)"
          >
            CC
          </b-button>
          <b-button
            variant="info"
            class="flex-1"
            @click="onCloseRent(PAYMENT_METHODS_IDS.DEBIT_CARD)"
          >
            CD
          </b-button>
          <b-button
            variant="secondary"
            class="flex-1"
            @click="onCloseRent(PAYMENT_METHODS_IDS.PIX)"
          >
            PIX
          </b-button>
          <b-button
            variant="outline-info"
            class="flex-1"
            @click="onCloseRent()"
          >
            <span class="fas fa-plus-circle" />
          </b-button>
        </div>
      </div>
    </b-card-text>

    <template v-if="!disabled" #footer>
      <div class="actions d-flex flex-column gap-0">
        <b-button
          v-if="isPaused"
          variant="outline"
          style="border-radius: 0 0 0.2rem 0.2rem !important"
          class="w-100 text-success"
          @click="onTriggerAction(RENT_ACTIONS.RESUME)"
        >
          <span class="fas fa-play mr-1" />
          Resumir
        </b-button>
        <template v-else-if="isActive">
          <b-button
            variant="outline"
            class="w-100 rounded-0 text-muted"
            @click="onTriggerAction(RENT_ACTIONS.PAUSE)"
          >
            <span class="fas fa-pause mr-1" />
            Pausar
          </b-button>
        </template>
        <div v-else class="w-100">
          <b-button
            variant="success"
            class="w-100 rounded-0"
            @click="handleStart"
          >
            <span class="fas fa-flag-checkered mr-1" />
            Iniciar
          </b-button>
          <b-button
            variant="outline-success"
            style="border-radius: 0 0 0.2rem 0.2rem !important"
            class="w-100 rounded-0"
            :class="{ 'btn-darken': !isReopenButtonEnabled }"
            :disabled="!isReopenButtonEnabled"
            @click="onTriggerAction(RENT_ACTIONS.REOPEN_LAST_RENT)"
          >
            <i v-if="!isReopenButtonEnabled" class="fas fa-lock"></i>
            <span v-if="isReopenButtonEnabled" class="fas fa-redo mr-1" />
            Voltar último aluguel
          </b-button>
        </div>
      </div>
    </template>

    <customer-info-modal
      v-if="(isActive || isPaused) && toy.rent && toy.rent.customer"
      :customer="toy.rent.customer"
      :visible="showCustomerModal"
      :store-id="toy.storeId"
      @close="showCustomerModal = false"
      @points-adjusted="onCustomerPointsAdjusted"
    />

    <b-modal
      v-model="rescuePhotoModalVisible"
      size="lg"
      hide-footer
      @hide="resetRescuePhotoModal"
    >
      <template #modal-title>
        <span class="rescue-modal-title">
          <span class="fas fa-id-card rescue-modal-title-icon" />
          Documento para resgate
        </span>
      </template>

      <b-alert v-if="rescuePhotoError" show variant="danger">
        <span class="fas fa-exclamation-triangle mr-1" />
        {{ rescuePhotoError }}
      </b-alert>

      <div class="rescue-document-panel">
        <div class="rescue-document-summary mb-3">
          <span class="rescue-document-summary-icon fas fa-user-check" />
          <div>
            <small class="text-muted d-block">Cliente do resgate</small>
            <strong>
              {{
                (toy.rent && toy.rent.customer && toy.rent.customer.name) ||
                'Cliente não encontrado'
              }}
            </strong>
            <small class="text-muted d-block">
              CPF: <strong>{{ rescueCustomerDocument }}</strong>
            </small>
          </div>
        </div>

        <b-alert show variant="warning" class="rescue-document-warning">
          <span class="fas fa-exclamation-triangle mr-1" />
          A foto precisa ser de um documento do mesmo CPF cadastrado no perfil
          do cliente:
          <strong> {{ rescueCustomerDocument }}.</strong>
        </b-alert>

        <div class="rescue-document-stage mb-3">
          <div v-if="cameraActive" class="rescue-camera-frame">
            <video
              ref="rescueVideo"
              class="rescue-camera-video"
              autoplay
              muted
              playsinline
            />
          </div>

          <div v-else-if="rescuePhotoPreview" class="rescue-photo-preview">
            <img :src="rescuePhotoPreview" alt="Documento do resgate" />
            <span class="rescue-photo-status">
              <span class="fas fa-check-circle mr-1" />
              Foto pronta para confirmar
            </span>
          </div>

          <div v-else class="rescue-document-empty">
            <span class="rescue-document-empty-icon fas fa-camera" />
            <strong>Adicione a foto do documento</strong>
            <small class="text-muted">
              Fotografe um documento do titular do CPF
              {{ rescueCustomerDocument }}.
            </small>
          </div>
        </div>
      </div>

      <input
        v-if="isAdmin"
        ref="rescueFileInput"
        type="file"
        accept="image/*"
        class="d-none"
        @change="onRescuePhotoSelected"
      />

      <div class="rescue-actions">
        <div class="rescue-actions-group">
          <b-button
            variant="outline-primary"
            :disabled="rescuePhotoSubmitting"
            @click="startRescueCamera"
          >
            <span class="fas fa-camera mr-1" />
            {{ cameraActive ? 'Reiniciar camera' : 'Tirar foto' }}
          </b-button>

          <b-button
            v-if="cameraActive"
            variant="primary"
            :disabled="rescuePhotoSubmitting"
            @click="captureRescuePhoto"
          >
            <span class="fas fa-dot-circle mr-1" />
            Capturar
          </b-button>

          <b-button
            v-if="isAdmin"
            variant="outline-secondary"
            :disabled="rescuePhotoSubmitting"
            @click="$refs.rescueFileInput.click()"
          >
            <span class="fas fa-image mr-1" />
            Escolher imagem
          </b-button>
        </div>

        <b-button
          variant="success"
          class="rescue-confirm-button"
          :disabled="
            !toy.rent ||
            !toy.rent.customer ||
            !rescuePhotoFile ||
            rescuePhotoSubmitting
          "
          @click="confirmRescueRent"
        >
          <span class="fas fa-check mr-1" />
          Confirmar resgate
        </b-button>
      </div>
    </b-modal>
  </b-card>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { PAYMENT_METHODS, PAYMENT_METHODS_IDS, RENT_ACTIONS } from '@/constants'
import { format } from 'date-fns'
import { formatCurrency } from '@/helpers/numbers'
import CustomerInfoModal from './partials/CustomerInfoModal.vue'
import {
  calculateEndDate,
  calculatePeriod,
  isActive,
  isDelayed,
  isPaused
} from '@/helpers/rents'

export default {
  name: 'ToyRentListItem',
  components: {
    CustomerInfoModal
  },
  filters: {
    formatCurrency
  },
  props: {
    toy: {
      type: Object,
      required: true
    },
    disabled: {
      type: Boolean
    },
    selectionModeActive: {
      type: Boolean,
      default: false
    },
    checkoutsByStore: {
      type: Object,
      required: true
    },
    storePeriods: {
      type: Array,
      default: () => []
    },
    loyaltyDiscountEnabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showCustomerModal: false,
      lastTap: 0,
      tapTimeout: null,
      elapsedTime: 0,
      rentAmount: 0,
      productsAmount: 0,
      currentPausedTime: 0,
      rescuePhotoModalVisible: false,
      rescuePhotoFile: null,
      rescuePhotoPreview: null,
      rescuePhotoError: null,
      rescuePhotoSubmitting: false,
      rescueCameraStream: null,
      cameraActive: false
    }
  },
  computed: {
    ...mapGetters('store', ['isAdmin']),
    ...mapState('checkout', ['currentCheckout']),
    ...mapState('store', ['currentStore']),
    ...mapGetters('periods', ['periodsSortedByPrice']),
    isActive() {
      const rent = this.toy.rent
      return isActive(rent)
    },
    isPaused() {
      return isPaused(this.toy.rent)
    },
    isDelayed() {
      return isDelayed(this.endDate)
    },
    period() {
      return calculatePeriod(this.toy.rent)
    },
    createdAt() {
      if (this.toy.rent) {
        const createdAt = new Date(this.toy.rent.createdAt)
        return format(createdAt, "HH'h'mm")
      }
      return ''
    },
    endsAt() {
      return this.toy.rent ? format(this.endDate, "HH'h'mm") : ''
    },
    endDate() {
      return calculateEndDate(this.toy.rent)
    },
    extraTime() {
      return this.toy.rent.extraTime
        ?.filter(({ type }) => type !== 'pause')
        .reduce((acc, { minutes }) => acc + minutes, 0)
    },
    pausedTime() {
      return this.toy.rent.pausedTime
    },
    statusClass() {
      if (this.isPaused) {
        return 'alert-warning'
      }
      if (this.isDelayed) {
        return 'alert-danger'
      }
      return ''
    },
    amount() {
      return this.totalAmount
    },
    normalizedRentAmount() {
      return Number(this.toy.rent?.rentAmount || 0)
    },
    normalizedProductsAmount() {
      return Number(this.toy.rent?.productsAmount || this.productsAmount || 0)
    },
    totalAmount() {
      return this.normalizedRentAmount + this.normalizedProductsAmount
    },
    getElapsedTime() {
      return this.toy.rent.elapsedTime
    },
    getPoints() {
      return this.toy.rent.customer?.points?.total_rents_closed || 0
    },
    rentSequenceNumber() {
      return this.getPoints + 1
    },
    canRescueRent() {
      return this.rentSequenceNumber >= 11 && this.toy.rent?.period?.time <= 12
    },
    rescueCustomerDocument() {
      const document = String(this.toy.rent?.customer?.document || '')
      const digits = document.replace(/\D/g, '')

      if (digits.length !== 11) return document || 'não informado'

      return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4')
    },
    shouldShowLoyaltyDiscountOption() {
      return this.getPoints >= 10 && this.getElapsedTime > 12
    },
    loyaltyPeriod() {
      return (this.storePeriods || []).find(
        (period) => Number(period.time) === 12 && period.status === 'active'
      )
    },
    canApplyLoyaltyDiscount() {
      return Boolean(this.shouldShowLoyaltyDiscountOption && this.loyaltyPeriod)
    },
    loyaltyDiscountAmount() {
      if (!this.canApplyLoyaltyDiscount) return 0
      return Math.min(
        Number(this.loyaltyPeriod.price || 0),
        this.normalizedRentAmount
      )
    },
    isLoyaltyDiscountActive() {
      return Boolean(
        this.loyaltyDiscountEnabled && this.canApplyLoyaltyDiscount
      )
    },
    finalAmountWithLoyaltyDiscount() {
      return this.totalAmount - this.loyaltyDiscountAmount
    },
    isReopenButtonEnabled() {
      if (!this.toy.rents || !this.toy.rents[0]) {
        return false
      }
      const currentCheckoutId = this.checkoutsByStore?.[this.toy.storeId]?.id
      if (!this.toy.rents[0].checkoutId && this.toy.rents[0].canceledAt) {
        // Caso especial: aluguel cancelado antes de finalizar (sistema marca checkoutId apenas na finalização)
        return true
      }
      return this.toy.rents[0].checkoutId === currentCheckoutId
    },
    cancelDisabled() {
      const deadline = this.toy.rent.store.cancellationDeadline
      return deadline != null && this.getElapsedTime > deadline
    }
  },
  watch: {
    canApplyLoyaltyDiscount(value) {
      if (!value && this.loyaltyDiscountEnabled) {
        this.emitLoyaltyDiscount(false)
      }
    }
  },
  mounted() {
    console.log(`current user in ToyRentListItem: ${this.isAdmin}`)
  },
  beforeDestroy() {
    this.stopRescueCamera()
    this.revokeRescuePhotoPreview()
  },
  created() {
    this.RENT_ACTIONS = RENT_ACTIONS
    this.PAYMENT_METHODS_IDS = PAYMENT_METHODS_IDS
    this.PAYMENT_METHODS = PAYMENT_METHODS
  },
  methods: {
    handleCancelClick() {
      if (this.cancelDisabled && !this.isAdmin) {
        const deadline = this.toy.rent.store.cancellationDeadline
        this.$bvModal.msgBoxOk(
          `O tempo máximo de cancelamento é ${deadline} minutos.\nPara cancelar, contate um responsável.`,
          {
            title: 'Tempo de cancelamento excedido',
            okTitle: 'OK',
            centered: true,
            variant: 'danger',
            bodyClass: 'pre-line'
          }
        )
      } else {
        this.onTriggerAction(RENT_ACTIONS.CANCEL)
      }
    },
    onChangePeriod() {
      this.$emit('update-store-id', this.toy.storeId)
      this.onTriggerAction(RENT_ACTIONS.CHANGE_PERIOD)
    },
    handleStart() {
      this.$emit('update-store-id', this.toy.storeId)
      this.onTriggerAction(RENT_ACTIONS.START)
    },
    openCustomerModal() {
      if (!this.toy.rent?.customer) return

      this.showCustomerModal = true
    },
    onCustomerPointsAdjusted(points) {
      if (!this.toy.rent?.customer || !points) return

      this.$set(this.toy.rent.customer, 'points', points)
    },
    handleCardClick(event) {
      const currentTime = new Date().getTime()
      const tapLength = currentTime - this.lastTap
      clearTimeout(this.tapTimeout)
      if (tapLength < 300 && tapLength > 0) {
        this.handleDoubleClick(event)
      } else {
        this.tapTimeout = setTimeout(() => {
          this.handleSingleClick(event)
          clearTimeout(this.tapTimeout)
        }, 300)
      }
      this.lastTap = currentTime
    },
    handleDoubleClick() {
      if (this.toy.rent && (this.isActive || this.isPaused)) {
        this.$emit('activate-selection-mode', this.toy)
      }
    },
    handleSingleClick() {
      if (
        this.selectionModeActive &&
        (!this.toy.rent || (!this.isActive && !this.isPaused))
      ) {
        this.$emit('new-rental', this.toy)
      }
    },
    onAddExtraTime() {
      const { elapsedTime, rentAmount } = this
      this.onTriggerAction(RENT_ACTIONS.ADD_EXTRA_TIME, {
        elapsedTime,
        rentAmount
      })
    },
    onCloseRent(paymentMethod) {
      const hasProducts =
        this.toy.rent &&
        this.toy.rent.product &&
        this.toy.rent.product.length > 0
      const elapsedTime = this.getElapsedTime
      this.onTriggerAction(RENT_ACTIONS.CLOSE, {
        elapsedTime,
        hasProducts,
        paymentMethod,
        loyaltyDiscountEnabled: this.isLoyaltyDiscountActive
      })
    },
    emitLoyaltyDiscount(enabled) {
      this.$emit('update-loyalty-discount', {
        rentId: this.toy.rent.id,
        enabled
      })
    },
    onLoyaltyDiscountChange(value) {
      this.emitLoyaltyDiscount(Boolean(value && this.canApplyLoyaltyDiscount))
    },
    showLoyaltyDiscountInfo() {
      const discountText = this.loyaltyPeriod
        ? `Desconto atual: ${formatCurrency(this.loyaltyDiscountAmount)}.`
        : 'Para aplicar, cadastre um periodo ativo de 12 minutos.'
      this.$bvModal.msgBoxOk(
        [
          'Aplica o cartao fidelidade na finalizacao deste aluguel.',
          'O desconto usa o valor do periodo ativo de 12 minutos e desconta apenas do aluguel.',
          'Produtos nao recebem desconto.',
          'Ao finalizar, 10 pontos serao consumidos e este aluguel nao gera novo ponto.',
          discountText
        ].join('\n'),
        {
          title: 'Aplicar desconto',
          okTitle: 'OK',
          centered: true,
          bodyClass: 'pre-line'
        }
      )
    },
    async onRescueRent() {
      if (!this.toy.rent?.customer) return

      this.rescuePhotoError = null
      this.rescuePhotoModalVisible = true

      if (!this.isAdmin) {
        await this.$nextTick()
        this.startRescueCamera()
      }
    },
    async startRescueCamera() {
      this.rescuePhotoError = null
      this.stopRescueCamera()

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.rescuePhotoError =
          'Este dispositivo ou navegador nao permite abrir a camera. Use um dispositivo com camera.'
        return
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' }
          },
          audio: false
        })
        this.rescueCameraStream = stream
        this.cameraActive = true

        await this.$nextTick()
        if (this.$refs.rescueVideo) {
          this.$refs.rescueVideo.srcObject = stream
          await this.$refs.rescueVideo.play()
        }
      } catch (error) {
        this.cameraActive = false
        this.rescuePhotoError = this.getCameraErrorMessage(error)
      }
    },
    captureRescuePhoto() {
      const video = this.$refs.rescueVideo

      if (!video || !video.videoWidth || !video.videoHeight) {
        this.rescuePhotoError =
          'Nao foi possivel capturar a foto. Aguarde a camera carregar e tente novamente.'
        return
      }

      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const context = canvas.getContext('2d')
      context.drawImage(video, 0, 0, canvas.width, canvas.height)

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            this.rescuePhotoError =
              'Nao foi possivel gerar a imagem do documento.'
            return
          }

          const file = new File([blob], `documento-resgate-${Date.now()}.jpg`, {
            type: 'image/jpeg'
          })
          this.setRescuePhotoFile(file)
          this.stopRescueCamera()
        },
        'image/jpeg',
        0.9
      )
    },
    onRescuePhotoSelected(event) {
      if (!this.isAdmin) return

      const file = event.target.files && event.target.files[0]
      if (!file) {
        this.rescuePhotoError = 'Nenhuma imagem foi selecionada.'
        return
      }

      this.setRescuePhotoFile(file)
      event.target.value = ''
    },
    setRescuePhotoFile(file) {
      this.rescuePhotoError = null

      if (!file.type || !file.type.startsWith('image/')) {
        this.rescuePhotoError = 'Selecione uma imagem valida do documento.'
        return
      }

      if (file.size > 8 * 1024 * 1024) {
        this.rescuePhotoError = 'A foto do documento deve ter no maximo 8 MB.'
        return
      }

      this.revokeRescuePhotoPreview()
      this.rescuePhotoFile = file
      this.rescuePhotoPreview = URL.createObjectURL(file)
    },
    async confirmRescueRent() {
      if (!this.toy.rent?.customer) {
        this.rescuePhotoError = 'Cliente não identificado.'
        return
      }

      if (!this.rescuePhotoFile) {
        this.rescuePhotoError =
          'Tire uma foto do documento para concluir o resgate.'
        return
      }

      this.onTriggerAction(RENT_ACTIONS.RESCUE, {
        documentPhoto: this.rescuePhotoFile
      })
    },
    stopRescueCamera() {
      if (this.rescueCameraStream) {
        this.rescueCameraStream.getTracks().forEach((track) => track.stop())
      }
      this.rescueCameraStream = null
      this.cameraActive = false
    },
    revokeRescuePhotoPreview() {
      if (this.rescuePhotoPreview) {
        URL.revokeObjectURL(this.rescuePhotoPreview)
      }
      this.rescuePhotoPreview = null
    },
    resetRescuePhotoModal() {
      this.stopRescueCamera()
      this.revokeRescuePhotoPreview()
      this.rescuePhotoFile = null
      this.rescuePhotoError = null
      this.rescuePhotoSubmitting = false
    },
    getCameraErrorMessage(error) {
      if (error && error.name === 'NotAllowedError') {
        return 'Permita o acesso a camera para continuar o resgate.'
      }

      if (error && error.name === 'NotFoundError') {
        return 'Nao encontramos camera disponivel neste dispositivo.'
      }

      if (error && error.name === 'NotReadableError') {
        return 'A camera esta em uso por outro aplicativo. Feche-o e tente novamente.'
      }

      return 'Nao foi possivel abrir a camera. Verifique as permissoes e tente novamente.'
    },
    onTriggerAction(action, data) {
      this.$emit('trigger-action', { action, toy: this.toy, data })
    }
  }
}
</script>

<style lang="scss" scoped>
.chip {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.75rem;
  background: #fff;
}
.chip-muted {
  opacity: 0.7;
}
.loyalty-card-toggle {
  align-items: center;
  background: #f8f9fa;
  border: 1px solid #ced4da;
  border-radius: 0.35rem;
  display: inline-flex;
  gap: 0.35rem;
  margin-left: auto;
  padding: 0.18rem 0.35rem;
}

.loyalty-card-toggle.active {
  background: #e8f6ee;
  border-color: #7cc89a;
}

.loyalty-card-check {
  align-items: center;
  display: flex;
}

.loyalty-card-check ::v-deep .custom-control-label {
  display: flex;
  align-items: center;
}

.loyalty-card-label {
  align-items: center;
  display: inline-flex;
  gap: 0.35rem;
  line-height: 1.05;
}

.loyalty-card-icon {
  color: #198754;
  font-size: 0.9rem;
}

.loyalty-card-value {
  color: #198754;
  font-weight: 700;
}

.loyalty-info-button {
  color: #495057;
  line-height: 1;
}

.icon-wrapper {
  position: relative;
  display: inline-block;
}
.icon-wrapper::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  width: 3px;
  background: red;
  transform: rotate(-20deg);
}

.btn-darken {
  filter: brightness(0.1);
}

.card-footer {
  padding: 0px;
}

.actions {
  column-count: 4;
}

.card-title {
  font-size: 1rem;
  font-weight: bold;
}

.display-table {
  display: table;
}
.display-table > div {
  display: table-row;
}
.display-table > div > div {
  display: table-cell;
}

.card {
  width: 100%;
  max-width: none;
  height: 100%; /* opcional: para cards com mesma altura na coluna */
}

/* opcional: evita overflow interno que impeça o item de encolher/expandir */
:host,
.card {
  min-width: 0;
}

.toy-name {
  font-size: calc(1.7vh);
}

.btn-cancel {
  position: absolute;
  right: 10px;
  top: 10px;
}

.btn {
  font-size: 13px;
}

.rescue-modal-title {
  align-items: center;
  display: inline-flex;
  gap: 0.5rem;
}

.rescue-modal-title-icon {
  align-items: center;
  background: #e8f2ff;
  border-radius: 50%;
  color: #0d6efd;
  display: inline-flex;
  height: 2rem;
  justify-content: center;
  width: 2rem;
}

.rescue-document-panel {
  background: #fff;
}

.rescue-document-summary {
  align-items: center;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  display: flex;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
}

.rescue-document-summary-icon {
  align-items: center;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 50%;
  color: #495057;
  display: inline-flex;
  flex: 0 0 auto;
  height: 2.25rem;
  justify-content: center;
  width: 2.25rem;
}

.rescue-document-stage {
  background: #f8f9fa;
  border: 1px dashed #ced4da;
  border-radius: 0.5rem;
  min-height: 280px;
  overflow: hidden;
}

.rescue-camera-frame,
.rescue-photo-preview {
  background: #111827;
  height: 100%;
  min-height: 280px;
  position: relative;
  overflow: hidden;
}

.rescue-camera-video,
.rescue-photo-preview img {
  display: block;
  max-height: 52vh;
  min-height: 280px;
  object-fit: contain;
  width: 100%;
}

.rescue-photo-status {
  align-items: center;
  background: rgba(25, 135, 84, 0.94);
  border-radius: 999px;
  bottom: 0.75rem;
  color: #fff;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 700;
  left: 0.75rem;
  padding: 0.35rem 0.7rem;
  position: absolute;
}

.rescue-document-empty {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 280px;
  padding: 2rem;
  text-align: center;
}

.rescue-document-empty-icon {
  align-items: center;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 50%;
  color: #6c757d;
  display: inline-flex;
  font-size: 1.6rem;
  height: 4rem;
  justify-content: center;
  margin-bottom: 0.85rem;
  width: 4rem;
}

.rescue-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: space-between;
}

.rescue-actions-group {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.rescue-confirm-button {
  min-width: 170px;
}

@media (max-width: 575.98px) {
  .rescue-actions,
  .rescue-actions-group,
  .rescue-actions .btn {
    width: 100%;
  }

  .rescue-confirm-button {
    min-width: 0;
  }
}
</style>
